<template>
  <q-banner
    v-ripple="clickable"
    :class="bannerDefinedClasses"
    :dense="dense"
    :inline-actions="inlineActions"
    :rounded="rounded"
    @click="onClick"
  >
    <template v-if="icon || hasAvatarSlot" #avatar>
      <q-icon v-if="icon" :name="icon" />
      <slot v-else name="avatar" />
    </template>

    <div :class="contentClasses">
      <q-item-label v-if="label">
        {{ label }}
      </q-item-label>

      <q-item-label
        v-if="caption"
        caption
        :class="{'text-black': !dark, 'text-white': dark}"
      >
        {{ caption }}
      </q-item-label>

      <slot />
    </div>

    <template v-if="hasActionSlot" #action>
      <slot name="action" />
    </template>
  </q-banner>
</template>

<script>
/**
 * Used for displaying a banner within another component
 * Implements QBanner
 *
 * SLOTS:
 * - avatar: icon/img to display on left side
 * - default: text/content to display in the banner
 * - action: action button to display at right
 *
 * EMIT:
 * - click: if clickable=true, when banner is clicked
 */
export default {
  props: {
    // Classes to bind to banner content area
    contentClasses: {
      type: [String, Object]
    },
    // Alignment of content [left, center, right]
    align: {
      type: String,
      default: 'left'
    },
    // Icon to display on left
    icon: {
      type: String
    },
    // Dense banner
    dense: {
      type: Boolean,
      default: false
    },
    // Background color of banner
    color: {
      type: String,
      default: 'orange-2'
    },
    // Dark banner (white text on dark backgrounds)
    dark: {
      type: Boolean,
      default: false
    },
    // Shadow to apply to banner
    shadow: {
      type: Number,
      default: 0
    },
    // Is banner clickable
    clickable: {
      type: Boolean,
      default: false
    },
    // Show action buttons inline
    inlineActions: {
      type: Boolean,
      default: false
    },
    // Rounded borders on banner
    rounded: {
      type: Boolean,
      default: true
    },
    label: {
      type: String
    },
    caption: {
      type: String
    }
  },
  emits: ['click'],
  computed: {
    bannerDefinedClasses () {
      return {
        ['text-' + this.align]: !!this.align,
        ['bg-' + this.color]: !!this.color,
        ['shadow-' + this.shadow]: this.shadow > 0,
        'text-black': !this.dark,
        'text-white': this.dark,
        'cursor-pointer': this.clickable,
        'relative-position': true
      }
    },
    hasAvatarSlot () {
      return !!this.$slots.avatar
    },
    hasActionSlot () {
      return !!this.$slots.action
    }
  },
  methods: {
    onClick () {
      if (!this.clickable) {
        return
      }
      this.$emit('click')
    }
  }
}
</script>
